import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import MainNavbar from '../components/MainNavbar';
import MainFooter from '../components/MainFooter';
import blogPosts from '../data/blogData';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);

  useEffect(() => {
    // Find the post that matches the URL slug
    const foundPost = blogPosts.find(p => p.slug === slug);
    
    if (foundPost) {
      setPost(foundPost);
    } else {
      // Post not found, redirect to blog main page
      navigate('/blog');
    }
    
    // Scroll to top when post changes
    window.scrollTo(0, 0);
  }, [slug, navigate]);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  // Get related posts (simple version - just shows other posts in the same category)
  const relatedPosts = blogPosts
    .filter(p => p.id !== post.id && p.category === post.category)
    .slice(0, 3);

  return (
    <div className="font-sans antialiased text-gray-900">
      {/* Navbar */}
      <MainNavbar activePage="blog" />
      
      {/* Blog Post Content */}
      <article className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          {/* Back button */}
          <Link to="/blog" className="inline-flex items-center text-gray-600 hover:text-orange-500 mb-8">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
            Back to Blog
          </Link>
          
          {/* Post header */}
          <div className="text-center mb-12">
            <div className="mb-4">
              <span className="bg-orange-100 text-orange-800 text-sm px-3 py-1 rounded-full">
                {post.category}
              </span>
            </div>
            <h1 className="text-4xl font-bold mb-4">{post.title}</h1>
            <div className="text-gray-500 text-sm">
              <span>{post.date}</span>
            </div>
          </div>
          
          {/* Featured image */}
          <div className="mb-12">
            <img 
              src={post.image} 
              alt={post.title}
              className="w-full h-96 object-cover rounded-lg shadow-md"
            />
          </div>
          
          {/* Post content */}
          <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: post.content }} />
        </div>
      </article>
      
      {/* Related Articles */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl font-bold mb-8">Related Articles</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <div key={relatedPost.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={relatedPost.image} 
                      alt={relatedPost.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-bold mb-2">
                      <Link to={`/blog/${relatedPost.slug}`} className="hover:text-orange-500">
                        {relatedPost.title}
                      </Link>
                    </h3>
                    <div className="text-xs text-gray-500 mb-2">
                      <span>{relatedPost.date}</span>
                    </div>
                    <p className="text-gray-600 text-sm">{relatedPost.excerpt}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Design Process?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Get started with Dessino.ai today and create interactive prototypes in minutes.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Link to="/signup" className="px-8 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors">
              Start Free Trial
            </Link>
            <Link to="/features" className="px-8 py-3 bg-white text-gray-800 rounded-lg hover:bg-gray-100 transition-colors border border-gray-300">
              Learn More
            </Link>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <MainFooter />
    </div>
  );
};

export default BlogPost;